import type { FollowUpQuestion } from "@/types/chat-parts";
import { create } from "zustand";

export const CHAT_MODE = {
  INPUT: "input",
  ANSWERING: "answering",
} as const;

export type ChatMode = (typeof CHAT_MODE)[keyof typeof CHAT_MODE];

type ChatState = {
  suggestedQuestions: FollowUpQuestion[];
  userQuery: string;
  contextUrl?: string;
  mode: ChatMode;

  // Actions
  setUserQuery: (query: string) => void;
  setContextUrl: (url: string | undefined) => void;
  clearUserQuery: () => void;
  clearContextUrl: () => void;
  setMode: (mode: ChatMode) => void;
  resetState: () => void;
  submitQuery: (query: string) => void;
};

// Initial state
const initialState = {
  suggestedQuestions: [],
  userQuery: "",
  contextUrl: undefined,
  mode: CHAT_MODE.INPUT,
};

export const useChatState = create<ChatState>(set => ({
  ...initialState,

  // Update user query
  setUserQuery: (query: string) =>
    set(() => ({ userQuery: query })),

  // Update context URL
  setContextUrl: (url: string | undefined) =>
    set(() => ({ contextUrl: url })),

  // Clear user query
  clearUserQuery: () =>
    set(() => ({ userQuery: "" })),

  // Clear context URL
  clearContextUrl: () =>
    set(() => ({ contextUrl: undefined })),

  // Update chat mode
  setMode: (mode: ChatMode) =>
    set(() => ({ mode })),

  // Reset entire state
  resetState: () =>
    set(() => ({ ...initialState })),

  // Submit a query to initiate a new chat
  submitQuery: (query: string) =>
    set(() => ({
      userQuery: query,
      mode: CHAT_MODE.INPUT,
    })),
}));
